import { useEffect, useId, useRef, useState } from "react";
import type { FormEvent } from "react";
import { pilotNameSchema } from "@frontier/protocol/auth-contracts";

interface Props {
  name: string;
  disabled: boolean;
  onRename: (name: string) => Promise<boolean>;
}

export function PilotNameForm({ name, disabled, onRename }: Props) {
  const [draft, setDraft] = useState(name);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const pending = useRef(false);
  const mounted = useRef(true);
  const form = useRef<HTMLFormElement>(null);
  const input = useRef<HTMLInputElement>(null);
  const restoreFocus = useRef(false);
  const id = useId();
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  useEffect(() => {
    setDraft(name);
  }, [name]);
  useEffect(() => {
    if (disabled || saving || !restoreFocus.current) return;
    restoreFocus.current = false;
    if (
      form.current?.isConnected &&
      (document.activeElement === null ||
        document.activeElement === document.body)
    )
      input.current?.focus({ preventScroll: true });
  }, [disabled, saving]);
  const parsed = pilotNameSchema.safeParse(draft);
  const unchanged = parsed.success && parsed.data === name;
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (disabled || pending.current || unchanged) return;
    if (!parsed.success) {
      setError(
        "Ник: от 1 до 24 символов.",
      );
      return;
    }
    pending.current = true;
    const focusWasInside =
      form.current?.contains(document.activeElement) ?? false;
    setSaving(true);
    setError("");
    setMessage("");
    try {
      if ((await onRename(parsed.data)) && mounted.current) {
        setMessage("Ник сохранён.");
      }
    } catch (failure) {
      if (mounted.current)
        setError(
          failure instanceof Error
            ? failure.message
            : "Не удалось сохранить ник. Попробуй ещё раз.",
        );
    } finally {
      pending.current = false;
      if (mounted.current) {
        restoreFocus.current = focusWasInside;
        setSaving(false);
      }
    }
  };
  return (
    <form
      ref={form}
      className="pilot-name-form"
      onSubmit={(event) => void submit(event)}
      aria-label="Изменить ник"
    >
      <label htmlFor={id}>Ник</label>
      <div>
        <input
          ref={input}
          id={id}
          name="pilotName"
          autoComplete="nickname"
          spellCheck={false}
          minLength={1}
          maxLength={24}
          required
          value={draft}
          disabled={disabled || saving}
          aria-describedby={`${id}-hint`}
          aria-invalid={!parsed.success || !!error}
          onChange={(event) => {
            setDraft(event.target.value);
            setMessage("");
            setError("");
          }}
        />
        <button
          type="submit"
          disabled={disabled || saving || !parsed.success || unchanged}
        >
          {saving ? "Сохраняем…" : "Сохранить ник"}
        </button>
      </div>
      <small id={`${id}-hint`}>
        1–24 символа
      </small>
      {error && (
        <p className="account-error" role="alert">
          {error}
        </p>
      )}
      {message && <p role="status">{message}</p>}
    </form>
  );
}
